import { ArrowRight, FileCheck2, FileText, ShieldCheck, UserCheck, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthContext';

const seenKey = (userId: string) => `expertdevis.bienvenue.${userId}`;

/** Stockage indisponible : le message reste fermé, plutôt que de revenir à chaque page. */
function alreadySeen(userId: string) {
  try {
    return localStorage.getItem(seenKey(userId)) === '1';
  } catch {
    return true;
  }
}

/** Chaque promesse correspond à ce que l'application fait réellement : ne pas en ajouter sans le vérifier. */
const POINTS = [
  { icon: FileCheck2, title: 'Moins de risques d\'erreur', text: 'Totaux, TVA et montant en lettres sont recontrôlés avant chaque document.' },
  { icon: FileText, title: 'Une image professionnelle', text: 'Des devis et des factures clairs, à votre nom et avec votre logo.' },
  { icon: UserCheck, title: 'Vous gardez la décision', text: 'Vos prix restent les vôtres, et rien n\'est validé sans vous, pas même par l\'assistant.' },
  { icon: ShieldCheck, title: 'Vos données protégées', text: 'Elles sont séparées de celles des autres entreprises, et vous pouvez les exporter à tout moment.' },
];

/** Message d'accueil : une seule fois par personne et par appareil. */
export function WelcomeModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => !!user && !alreadySeen(user.id));

  const close = () => {
    try {
      if (user) localStorage.setItem(seenKey(user.id), '1');
    } catch {
      // Stockage indisponible : le message est fermé pour cette visite.
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!open || !user) return null;
  const firstName = user.fullName.split(' ')[0];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-navy-950/60 p-0 sm:items-center sm:p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="bienvenue-titre" className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl">
        <button type="button" className="btn-ghost absolute top-3 right-3 px-2" aria-label="Fermer" onClick={close}><X size={18} /></button>
        <p className="text-sm font-semibold text-brand-600">Bienvenue {firstName} !</p>
        <h2 id="bienvenue-titre" className="mt-1 pr-8 text-2xl leading-tight font-bold text-navy-900">Un devis juste protège votre marge et votre réputation.</h2>
        <p className="mt-2 text-sm text-muted">Une erreur de quantité ou de total peut coûter cher sur un chantier. ExpertDevis BTP vous aide à chiffrer avec méthode, à justifier vos prix et à présenter des documents qui inspirent confiance.</p>
        <ul className="mt-4 space-y-3 text-sm">
          {POINTS.map((p) => (
            <li key={p.title} className="flex gap-3">
              <p.icon size={18} aria-hidden="true" className="mt-0.5 shrink-0 text-brand-600" />
              <span><strong className="font-semibold text-navy-900">{p.title}.</strong> {p.text}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5 flex flex-col gap-2">
          <button type="button" className="btn-accent w-full justify-center" onClick={close}>Commencer <ArrowRight size={16} aria-hidden="true" /></button>
          <button type="button" className="btn-ghost w-full justify-center" onClick={() => { close(); navigate('/aide?sujet=premiers-pas'); }}>Voir le guide de démarrage</button>
        </div>
      </div>
    </div>
  );
}
